import { serialize } from "object-to-formdata"

/**
 * Converts form values → serialized API payload for steps that use pure form state.
 *
 * Steps with non-form local state (scheduling, media, location, resources, digital_waiver)
 * register their own save handlers via stepSaveHandlersRef — this file only handles
 * the steps where form values alone are sufficient.
 *
 * IMPORTANT: API keys here must match existing backend contract exactly.
 */

/**
 * Details step payload.
 * Maps multi-value string arrays to the _attributes format expected by Rails.
 * selectedLanguages / selectedSkillLevels come from ExperienceDetails callbacks
 * (those components don't use Form.Item onChange, they use prop callbacks).
 *
 * @param {object} formValues
 * @param {object} options
 * @param {Array}  options.availableCategories - fetched category list from /guide/categories [{ id, name }]
 */
export const buildDetailsPayload = (formValues, { experienceType } = {}) => {
  const { categories: rawCategories, included_items = [], bring_items = [], languages = [], skill_levels = [], marketplace_tags = [], ...rest } = formValues

  // categories form value is a single ID (number) — wrap in array for the API
  const category_ids = rawCategories ? [rawCategories] : []

  return serialize(
    {
      trip: {
        ...rest,
        ...(experienceType ? { experience_type: experienceType } : {}),
        language_ids: languages,
        skill_level_ids: skill_levels,
        marketplace_tag_ids: marketplace_tags,
        category_ids,
        included_item_ids: included_items,
        bring_item_ids: bring_items
      }
    },
    { allowEmptyArrays: true }
  )
}

/**
 * Preview step payload — marks the wizard as complete.
 */
export const buildPreviewPayload = () =>
  serialize({ trip: { wizard_completed: true } }, { allowEmptyArrays: true })

/**
 * Scheduling step payload builder (used inside SchedulingStep's registered handler).
 * Extracted here for testability and co-location with other payload builders.
 */
export const buildSchedulingPayload = (formValues, { isSeasonalExperience, timeBlocks, isTicketed = false }) => {
  const updateData = {
    time_zone: formValues.time_zone,
  }

  if (isTicketed) {
    updateData.duration_value = formValues.duration_value
    updateData.duration_unit = formValues.duration_unit
    updateData.cut_off_value = formValues.cut_off_value
    updateData.cut_off_unit = formValues.cut_off_unit
    updateData.low_availability_enabled = formValues.low_availability_enabled
    updateData.low_availability_threshold = formValues.low_availability_threshold
    // time_slots entries have { id, label, starts_at } — send starts_at to backend
    updateData.scheduled_slots_attributes = (formValues.time_slots || []).map(({ id, starts_at, _destroy }) => ({
      ...(isServerId(id) ? { id } : {}),
      starts_at,
      ...(_destroy ? { _destroy: true } : {})
    }))
  } else {
    updateData.duration_type = formValues.duration_type
    updateData.cut_off_value = formValues.cut_off_value
    updateData.cut_off_unit = formValues.cut_off_unit
    updateData.seasonal = isSeasonalExperience

    if (isSeasonalExperience) {
      updateData.recurring_yearly = formValues.recurringYearly

      const dateRanges = timeBlocks
        .map((blockIndex) => {
          const start = formValues[`seasonStart_${blockIndex}`]
          const end = formValues[`seasonEnd_${blockIndex}`]
          if (start && end) {
            return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") }
          }
          return null
        })
        .filter(Boolean)

      const seasonalTripsAttributes = timeBlocks
        .map((blockIndex) => {
          const start = formValues[`seasonStart_${blockIndex}`]
          const end = formValues[`seasonEnd_${blockIndex}`]
          if (start && end) {
            return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") }
          }
          return null
        })
        .filter(Boolean)

      if (dateRanges.length > 0) {
        updateData.available_date_ranges = dateRanges
        updateData.seasonal_trips_attributes = seasonalTripsAttributes
      }
    }

    updateData.availability_schedules_attributes = (formValues.availability_schedules || []).map((s) => ({
      ...(isServerId(s.id) ? { id: s.id } : {}),
      name: s.name ?? null,
      days: s.days ?? [],
      from_time: s.from_time ?? null,
      to_time: s.to_time ?? null,
      interval: s.interval ?? null,
      start_times: s.start_times ?? [],
    }))

    updateData.schedule_overrides_attributes = (formValues.schedule_overrides || []).map((o) => ({
      ...(isServerId(o.id) ? { id: o.id } : {}),
      name: o.name ?? null,
      date_ranges: o.date_ranges ?? [],
      from_time: o.from_time ?? null,
      to_time: o.to_time ?? null,
      interval: o.interval ?? null,
      start_times: o.start_times ?? [],
    }))
  }

  return serialize(
    { trip: updateData },
    { allowEmptyArrays: true }
  )
}

/**
 * Location step payload builder (used inside LocationStep's registered handler).
 */
export const buildLocationPayload = ({ additional_directions, meetingLatLng, coordinates }) =>
  serialize(
    {
      trip: {
        additional_directions: additional_directions || "",
        latitude: coordinates?.lat,
        longitude: coordinates?.lng,
        address: meetingLatLng
      }
    },
    { allowEmptyArrays: true }
  )

/**
 * Resources step payload builder (used inside ResourcesStep's registered handler).
 */
export const buildResourcesPayload = ({ selectedEquipment, selectedCrewMembers, autoAssignEquipment }) =>
  serialize(
    {
      trip: {
        boat_ids: selectedEquipment.filter((id) => id !== "all").map(Number),
        assigned_staff_ids: selectedCrewMembers.filter((id) => id !== "all").map(Number),
        auto_assign_equipment: autoAssignEquipment
      }
    },
    { allowEmptyArrays: true }
  )

/**
 * Media step payload builder (used inside MediaStep's registered handler).
 */
/**
 * Returns true for real server IDs (small integers).
 * Date.now() temp IDs are ~1.7e12 and are excluded so new records don't send stale client IDs.
 */
export const isServerId = (id) => typeof id === "number" && id < 1e12

/**
 * Ticket Type step payload.
 */
export const buildTicketTypePayload = (formValues) => {
  const { ticket_types = [], ticket_duration, min_tickets, max_tickets, ticket_description } = formValues
  return serialize(
    {
      trip: {
        ticket_types_attributes: ticket_types.map(({ id, title, description, enabled, _destroy }) => {
          if (_destroy) return { id, _destroy: true }
          return {
            ...(isServerId(id) ? { id } : {}),
            title,
            description,
            enabled,
          }
        }),
        ticket_duration,
        min_tickets,
        max_tickets,
        ticket_description
      }
    },
    { allowEmptyArrays: true, indices: true }
  )
}

/**
 * Pricing step payload — payment type, base ticket prices, day/seasonal adjustments, fees.
 */
export const buildPricingPayload = (formValues) => {
  const {
    dayAdjustments = [],
    seasonalAdjustments = [],
    pricingFees = [],
    payment_type,
    deposit_percentage,
    ticketTypePrices = {},
    ticket_types = [],
  } = formValues
  const mapId = (id) => (isServerId(id) ? { id } : {})

  const ticket_type_prices_attributes = ticket_types
    .map(({ id }) => {
      const entry = ticketTypePrices[id]
      if (!entry) return null
      return {
        ...(isServerId(id) ? { ticket_type_id: id } : {}),
        price: entry.price,
        enabled: entry.enabled !== false,
      }
    })
    .filter(Boolean)

  const tripPayload = {
    wizard_step: "ticketed_pricing",
    payment_type,
    ...(payment_type === "deposit" ? { deposit_percent: deposit_percentage } : {}),
    ticket_type_prices_attributes,
  }

  if (dayAdjustments.length > 0) {
    tripPayload.day_adjustments_attributes = dayAdjustments.map(
      ({ id, name, appliesOn, customDays, direction, priceValue, priceType, affects, selectedTickets, _destroy }) => ({
        ...mapId(id),
        name,
        applies_on: appliesOn,
        custom_days: customDays || [],
        direction,
        price_value: priceValue,
        price_type: priceType,
        affects,
        selected_ticket_ids: selectedTickets || [],
        ...(_destroy ? { _destroy: true } : {})
      })
    )
  }

  if (seasonalAdjustments.length > 0) {
    tripPayload.seasonal_adjustments_attributes = seasonalAdjustments.map(
      ({ id, name, startDate, endDate, isRepeatEachYear, direction, priceValue, priceType, affects, selectedTickets, _destroy }) => ({
        ...mapId(id),
        name,
        start_date: startDate?.format?.("YYYY-MM-DD") ?? startDate,
        end_date: endDate?.format?.("YYYY-MM-DD") ?? endDate,
        is_repeat_each_year: isRepeatEachYear ?? true,
        direction,
        price_value: priceValue,
        price_type: priceType,
        affects,
        selected_ticket_ids: selectedTickets || [],
        ...(_destroy ? { _destroy: true } : {})
      })
    )
  }

  if (pricingFees.length > 0) {
    tripPayload.trip_tax_fee_ids = pricingFees.map((f) => f.id)
  }

  return serialize({ trip: tripPayload }, { allowEmptyArrays: true })
}

export const buildMediaPayload = (localMedia) =>
  serialize(
    {
      trip: {
        media_attributes: localMedia.map(({ id, file_type, blob, _destroy, position, file }) => ({
          file_type,
          _destroy,
          position,
          ...(file ? { file } : { id }),
          ...(file_type === "video" || file_type === "trimmed_video" ? { preview: blob } : {})
        }))
      }
    },
    { indices: true }
  )

/**
 * Additional Info step — combined payload for waivers, question templates, and add-ons.
 *
 * @param {Array}  addOns                - Current add-on list from form field "add_ons"
 * @param {Array}  serverAddOnIds        - IDs that existed on the server when the step mounted
 * @param {Array}  digitalWaiverIds      - Selected waiver IDs from form field "digital_waiver"
 * @param {Array}  questionTemplateIds   - Selected template IDs from form field "question_template"
 */
export const buildAdditionalInfoPayload = (addOns, serverAddOnIds = [], digitalWaiverIds = [], questionTemplateIds = []) => {
  const currentServerIds = new Set(addOns.filter((a) => isServerId(a.id)).map((a) => a.id))

  const addOnsAttributes = [
    ...addOns.map(({ id, title, category, price, minPerOrder, maxPerOrder, description, enabled, photoFile }) => ({
      ...(isServerId(id) ? { id } : {}),
      title,
      add_on_category_id: category,
      price,
      ...(minPerOrder == null ? {} : { min_per_order: minPerOrder }),
      ...(maxPerOrder == null ? {} : { max_per_order: maxPerOrder }),
      description,
      enabled: enabled !== false,
      ...(photoFile instanceof File ? { photo: photoFile } : {})
    })),
    ...serverAddOnIds.filter((id) => !currentServerIds.has(id)).map((id) => ({ id, _destroy: true }))
  ]

  return serialize(
    {
      trip: {
        add_ons_attributes: addOnsAttributes,
        digital_waiver_id: digitalWaiverIds,
        question_template_id: questionTemplateIds
      }
    },
    { allowEmptyArrays: true, indices: true }
  )
}

/**
 * Additional Info step — add-ons payload.
 *
 * @param {Array}  addOns          - Current add-on list from form field "add_ons"
 * @param {Array}  serverAddOnIds  - IDs that existed on the server when the step mounted;
 *                                   any not present in addOns will be sent with _destroy: true
 */
export const buildAddOnsPayload = (addOns, serverAddOnIds = []) => {
  const currentServerIds = new Set(addOns.filter((a) => isServerId(a.id)).map((a) => a.id))

  const attributes = [
    ...addOns.map(({ id, title, category, price, minPerOrder, maxPerOrder, description, enabled, photoFile }) => ({
      ...(isServerId(id) ? { id } : {}),
      title,
      add_on_category_id: category,
      price,
      ...(minPerOrder == null ? {} : { min_per_order: minPerOrder }),
      ...(maxPerOrder == null ? {} : { max_per_order: maxPerOrder }),
      description,
      enabled: enabled !== false,
      ...(photoFile instanceof File ? { photo: photoFile } : {})
    })),
    // Soft-delete server records the user removed locally
    ...serverAddOnIds.filter((id) => !currentServerIds.has(id)).map((id) => ({ id, _destroy: true }))
  ]

  return serialize({ trip: { add_ons_attributes: attributes } }, { allowEmptyArrays: true, indices: true })
}
